import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import type { EChartsOption } from 'echarts';
import type { EChartsType } from 'echarts/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import {
  ICircularGraphData,
  ICircularGraphLegendEntry,
  IColorMap,
  TCircularGraphNameOverrides,
  TCircularGraphStatusType,
} from './circular-graph.interface';
import { CircularGraphService } from './circular-graph.service';

@Component({
  imports: [
    DecimalPipe,
    MatTooltipModule,
    NgTemplateOutlet,
    NgxEchartsDirective,
    NgxSkeletonLoaderModule,
    TextClampTooltipDirective,
    TranslateModule,
  ],
  providers: [CircularGraphService],
  selector: 'app-circular-graph',
  templateUrl: './circular-graph.component.html',
})
export class CircularGraphComponent {
  public readonly data = input<ICircularGraphData>({ total: null });

  public readonly showPercentage = input<boolean>(true);
  public readonly loading = input<boolean>(false);
  public readonly includeBonusInMainSeries = input<boolean>(false);
  public readonly chartSize = input<number>(196);
  public readonly showTotal = input<boolean>(false);
  public readonly totalCountCentered = input<boolean>(false);
  public readonly totalCountFontClass = input<string>('font-bold text-3xl');
  public readonly totalLabelFontClass = input<string>('text-xs text-shikenso-purple-base');
  public readonly statusType = input<TCircularGraphStatusType>('default');
  public readonly colorMap = input<Partial<IColorMap>>({});
  /** Overrides the tooltip's displayed name per data slot - see `TCircularGraphNameOverrides`. */
  public readonly nameOverrides = input<TCircularGraphNameOverrides>({});

  /**
   * Opt-in internal legend rendered next to the ring - `null` (the default) renders nothing extra
   * and leaves the component's markup identical to before this input existed, so every consumer
   * that doesn't pass this is completely unaffected. Entries are shown in the same order as the
   * ring's segments (processing, success, failed, problem, bonus).
   */
  public readonly legendEntries = input<ICircularGraphLegendEntry[] | null>(null);
  /** Bolds the legend's value column - cosmetic only, off by default to match the legend's initial look. */
  public readonly legendValueBold = input<boolean>(false);
  /** How many placeholder rows the legend shows while `loading()` is true and no real entries are available yet. */
  public readonly legendSkeletonCount = input<number>(5);

  public readonly segmentClicked = output<string>();

  public readonly chartOption = signal<EChartsOption>({});
  public readonly chartFinished = signal(false);
  public readonly centerPercentage = signal<string>('0%');

  public readonly mergedColorMap = computed<IColorMap>(() => {
    return {
      ...this.circularGraphService.defaultColorMap,
      ...this.colorMap(),
    };
  });

  public readonly displayChart = computed(() => this.chartFinished() && !this.loading());

  public readonly actualTotal = computed((): number => {
    const d = this.data();
    const bonus = this.includeBonusInMainSeries() ? (d.bonus ?? 0) : 0;

    return (d.success ?? 0) + (d.failed ?? 0) + (d.problem ?? 0) + (d.processing ?? 0) + bonus;
  });

  public readonly skeletonSize = computed(() => {
    const size = this.chartSize();

    return Math.round(size * (166 / 196));
  });

  /** Fixed-size placeholder list the legend's loading skeleton iterates over. */
  public readonly legendSkeletonSlots = computed<number[]>(() =>
    Array.from({ length: this.legendSkeletonCount() }, (_unused, index) => index),
  );

  private readonly circularGraphService = inject(CircularGraphService);
  private readonly translate = inject(TranslateService);

  private readonly total = computed(() => {
    return this.data().total ?? 0;
  });

  private readonly bonusTotal = computed(() => {
    return this.data().bonus ?? 0;
  });

  private readonly percentage = computed(() => {
    const total = this.total();

    if (total === 0) {
      return 0;
    }

    const success = this.data().success ?? 0;
    const problem = this.data().problem ?? 0;

    if (success === 0 && problem === 0) {
      return 0;
    }

    return Math.round(((success + problem) / total) * 100);
  });

  private readonly legendSlotOrder: (keyof IColorMap)[] = ['processing', 'success', 'failed', 'problem', 'bonus'];

  private chartInstance: EChartsType | null = null;

  constructor() {
    this.initializeChart();

    effect(() => {
      const percentage = this.percentage();

      this.centerPercentage.set(this.data().total !== null ? `${percentage}%` : this.translate.instant('shared.na'));
      this.initializeChart();
    });
  }

  public onChartInit(chart: EChartsType): void {
    this.chartInstance = chart;
    this.setupTooltipStyleHandler(chart);
  }

  public onChartClick(params: unknown): void {
    const param = params as { name?: string };

    if (param.name && param.name !== 'remaining') {
      this.segmentClicked.emit(param.name);
    }
  }

  private setupTooltipStyleHandler(chart: EChartsType): void {
    const updateTooltipBorder = (params: unknown): void => {
      const param = params as { data?: { name: string } };

      setTimeout(() => {
        const tooltipDom = chart.getDom().querySelector('.echarts-tooltip');

        if (tooltipDom instanceof HTMLElement) {
          if (
            param?.data?.name === 'bonus' ||
            param?.data?.name === 'bonus-tip' ||
            param?.data?.name === 'bonus-empty'
          ) {
            tooltipDom.style.borderColor = this.mergedColorMap().bonus;
          } else {
            tooltipDom.style.borderColor = '';
          }
        }
      }, 0);
    };

    chart.on('showTip', updateTooltipBorder);
    chart.on('updateAxisPointer', updateTooltipBorder);
  }

  public onChartFinished(): void {
    this.chartFinished.set(true);
  }

  /**
   * The legend's swatch color for a given row index - looked up from the same colorMap driving
   * the ring, in the same slot order `CircularGraphService.buildMainSeriesData` renders segments
   * in, so a legend row's dot always matches the ring segment it describes.
   */
  public legendDotColor(index: number): string {
    const slot = this.legendSlotOrder[index];

    return slot ? this.mergedColorMap()[slot] : '';
  }

  private initializeChart(): void {
    const data = this.data();
    const total = this.total();
    const bonus = this.bonusTotal();
    const includeBonusInMainSeries = this.includeBonusInMainSeries();
    const size = this.chartSize();

    const mainSeriesData = this.circularGraphService.buildMainSeriesData(data, total, includeBonusInMainSeries);
    const bonusSeriesData = includeBonusInMainSeries
      ? []
      : this.circularGraphService.buildBonusSeriesData(bonus, total);

    const allSeries = [
      ...this.circularGraphService.buildMainSeries(mainSeriesData, size, this.colorMap()),
      ...this.circularGraphService.buildBonusSeries(bonusSeriesData, total, size),
    ];

    const newOptions: EChartsOption = {
      backgroundColor: 'transparent',
      series: allSeries.length > 0 ? allSeries : [],
      tooltip: {
        borderWidth: 1,
        confine: true,
        formatter: (params: unknown) =>
          this.circularGraphService.getTooltipFormatter(params, bonus, total, this.statusType(), this.nameOverrides()),
        trigger: 'item',
      },
    };

    this.chartOption.set(newOptions);
  }
}
